import { StoredPacket } from '../storage/db';
import { getNodeColor, snrToOpacity } from './nodeColor';

/**
 * Optional resolver function to extract the transmitting node identity and color for a packet.
 *
 * Why: Resolves whether the RF last hop was a matched 8-bit relay or the original sender,
 * guaranteeing identical chromatic identity between raw spot markers and aggregated hex cells.
 */
export type TransmitterResolver = (packet: StoredPacket) => {
  nodeId: number;
  nodeName: string;
  color: string;
};

/**
 * Default visual pixel radius of hexagons on screen.
 * Radius = distance from hexagon center to any of the 6 vertices.
 * 26px yields an ergonomic, touch-friendly touch target (~45px total width)
 * that aggregates dense telemetry breadcrumbs without obscuring basemap geography.
 */
export const DEFAULT_HEX_RADIUS_PX = 26;

/**
 * Node aggregation summary within a single hexagonal cell.
 */
export interface HexNodeSummary {
  nodeId: number;
  nodeName: string;
  count: number;
  maxSnr: number;
  latestRssi: number;
  color: string;
}

/**
 * Structured data describing an individual aggregated hexagon on the tactical map.
 */
export interface HexCellData {
  id: string; // `${zoom}_${q}_${r}`
  zoom: number;
  q: number;
  r: number;
  center: [number, number]; // [lat, lon]
  polygon: [number, number][]; // 6 [lat, lon] vertices
  packets: StoredPacket[];
  packetCount: number;
  dominantNodeId: number;
  dominantNodeName: string;
  color: string;
  maxSnr: number;
  avgSnr: number;
  opacity: number;
  isMultiNode: boolean;
  nodesSummary: HexNodeSummary[];
}

/**
 * Projects a WGS84 geographic coordinate (latitude, longitude) into Web Mercator continuous
 * pixel space at a specific integer map zoom level.
 *
 * Why: Leaflet maps use Web Mercator (EPSG:3857). Converting to pixel coordinates allows
 * screen-space hexagon binning where hexagon size remains physically constant regardless of zoom.
 *
 * @param lat - Latitude in degrees [-85.0511..85.0511]
 * @param lon - Longitude in degrees [-180..180]
 * @param zoom - Integer Leaflet map zoom level
 * @returns Projected pixel coordinates { x, y }
 */
export function latLonToWebMercatorPixel(
  lat: number,
  lon: number,
  zoom: number
): { x: number; y: number } {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const clampedSin = Math.max(-0.9999, Math.min(0.9999, sinLat));
  const y = (0.5 - Math.log((1 + clampedSin) / (1 - clampedSin)) / (4 * Math.PI)) * scale;
  return { x, y };
}

/**
 * Unprojects Web Mercator continuous pixel coordinates back into WGS84 (latitude, longitude).
 *
 * Why: Needed to convert computed hexagon polygon vertices from pixel space back into
 * geographic coordinates ready for Leaflet L.polygon rendering.
 *
 * @param x - Horizontal pixel coordinate
 * @param y - Vertical pixel coordinate
 * @param zoom - Integer Leaflet map zoom level
 * @returns Geographic coordinate { lat, lon }
 */
export function webMercatorPixelToLatLon(
  x: number,
  y: number,
  zoom: number
): { lat: number; lon: number } {
  const scale = 256 * Math.pow(2, zoom);
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lon };
}

/**
 * Rounds floating-point axial coordinates (q, r) to the nearest integer hexagon using
 * standard cube coordinate rounding (x + y + z = 0).
 *
 * Why: Floating-point division in hexagonal grids causes boundary ambiguities. Converting
 * to 3D cube coordinates guarantees unambiguous, gapless geometric tessellation.
 *
 * @param q - Fractional axial column
 * @param r - Fractional axial row
 * @returns Integer axial coordinates { q, r }
 */
export function cubeRound(q: number, r: number): { q: number; r: number } {
  const x = q;
  const z = r;
  const y = -x - z;

  let rx = Math.round(x);
  let ry = Math.round(y);
  let rz = Math.round(z);

  const xDiff = Math.abs(rx - x);
  const yDiff = Math.abs(ry - y);
  const zDiff = Math.abs(rz - z);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  return { q: rx === 0 ? 0 : rx, r: rz === 0 ? 0 : rz };
}

/**
 * Converts continuous pixel coordinates to discrete axial hexagon coordinates (q, r)
 * for a pointy-topped hexagonal grid of radius R.
 *
 * @param x - Pixel x coordinate
 * @param y - Pixel y coordinate
 * @param radiusPx - Hexagon radius in screen pixels
 * @returns Discretized axial indices { q, r }
 */
export function pixelToAxial(
  x: number,
  y: number,
  radiusPx: number
): { q: number; r: number } {
  const q = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / radiusPx;
  const r = ((2 / 3) * y) / radiusPx;
  return cubeRound(q, r);
}

/**
 * Computes the pixel center (cx, cy) of a pointy-topped hexagon from its axial indices (q, r).
 *
 * @param q - Axial column index
 * @param r - Axial row index
 * @param radiusPx - Hexagon radius in screen pixels
 * @returns Pixel center coordinates { x, y }
 */
export function axialToPixel(
  q: number,
  r: number,
  radiusPx: number
): { x: number; y: number } {
  const x = radiusPx * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r);
  const y = radiusPx * ((3 / 2) * r);
  return { x, y };
}

/**
 * Computes the 6 geographic vertices [lat, lon][] of a pointy-topped hexagon.
 *
 * Why: Generates standard Leaflet polygon coordinate arrays oriented with a point on top
 * (vertex angles at 30°, 90°, 150°, 210°, 270°, 330°).
 *
 * @param q - Axial column index
 * @param r - Axial row index
 * @param zoom - Current map zoom level
 * @param radiusPx - Hexagon radius in screen pixels
 * @returns Array of 6 [latitude, longitude] vertex tuples
 */
export function getHexagonPolygon(
  q: number,
  r: number,
  zoom: number,
  radiusPx: number = DEFAULT_HEX_RADIUS_PX
): [number, number][] {
  const centerPx = axialToPixel(q, r, radiusPx);
  const polygon: [number, number][] = [];

  for (let i = 0; i < 6; i++) {
    const angleRad = (Math.PI / 180) * (30 + i * 60);
    const vx = centerPx.x + radiusPx * Math.cos(angleRad);
    const vy = centerPx.y + radiusPx * Math.sin(angleRad);
    const { lat, lon } = webMercatorPixelToLatLon(vx, vy, zoom);
    polygon.push([lat, lon]);
  }

  return polygon;
}

/**
 * Computes the geographic center [lat, lon] of an axial hexagon.
 *
 * @param q - Axial column index
 * @param r - Axial row index
 * @param zoom - Current map zoom level
 * @param radiusPx - Hexagon radius in screen pixels
 * @returns [latitude, longitude] center tuple
 */
export function getHexagonCenter(
  q: number,
  r: number,
  zoom: number,
  radiusPx: number = DEFAULT_HEX_RADIUS_PX
): [number, number] {
  const centerPx = axialToPixel(q, r, radiusPx);
  const { lat, lon } = webMercatorPixelToLatLon(centerPx.x, centerPx.y, zoom);
  return [lat, lon];
}

/**
 * Manages dynamic spatial aggregation of LoRa packet reception points into a constant-pixel-size
 * hexagonal grid that smoothly adapts its geographic granularity across zoom levels.
 */
export class HexGridManager {
  private radiusPx: number;

  constructor(radiusPx = DEFAULT_HEX_RADIUS_PX) {
    this.radiusPx = radiusPx;
  }

  /**
   * Sets the visual pixel radius of hexagons.
   *
   * @param radiusPx - New radius in pixels
   */
  setRadiusPx(radiusPx: number): void {
    this.radiusPx = radiusPx;
  }

  /**
   * Retrieves the current hexagon visual pixel radius.
   */
  getRadiusPx(): number {
    return this.radiusPx;
  }

  /**
   * Bins an array of packets into hexagonal spatial cells for a given map zoom level.
   *
   * Why: Displaying raw tracking points becomes cluttered over prolonged sessions.
   * Grouping points into regular hexagons preserves visual clarity, reflects RF density,
   * highlights coverage overlap between multiple nodes, and scales naturally with zoom.
   *
   * @param packets - Array of stored packets with geographic coordinates
   * @param zoom - Current Leaflet zoom level (typically 1..19)
   * @param resolver - Optional node and color resolver function matching tracking point styling
   * @returns Array of aggregated HexCellData objects
   */
  binPackets(
    packets: StoredPacket[],
    zoom: number,
    resolver?: TransmitterResolver
  ): HexCellData[] {
    const cells = new Map<
      string,
      {
        q: number;
        r: number;
        packets: StoredPacket[];
      }
    >();

    // 1. Group packets into axial grid cells
    for (const p of packets) {
      if (!p.hasGeo || p.lat === null || p.lon === null) continue;
      const px = latLonToWebMercatorPixel(p.lat, p.lon, zoom);
      const { q, r } = pixelToAxial(px.x, px.y, this.radiusPx);
      const key = `${zoom}_${q}_${r}`;

      let cell = cells.get(key);
      if (!cell) {
        cell = { q, r, packets: [] };
        cells.set(key, cell);
      }
      cell.packets.push(p);
    }

    // 2. Synthesize cell statistics and geometry
    const result: HexCellData[] = [];

    for (const [key, cell] of cells.entries()) {
      const polygon = getHexagonPolygon(cell.q, cell.r, zoom, this.radiusPx);
      const center = getHexagonCenter(cell.q, cell.r, zoom, this.radiusPx);

      const nodeMap = new Map<
        number,
        {
          nodeId: number;
          nodeName: string;
          count: number;
          maxSnr: number;
          latestRssi: number;
          color: string;
        }
      >();

      let cellMaxSnr = -Infinity;
      let snrSum = 0;

      for (const p of cell.packets) {
        let nodeId: number;
        let nodeName: string;
        let color: string;

        if (resolver) {
          const resolved = resolver(p);
          nodeId = resolved.nodeId;
          nodeName = resolved.nodeName;
          color = resolved.color;
        } else {
          nodeId = p.relayNode && p.relayNode !== 0 ? p.relayNode & 0xff : p.from;
          nodeName = p.relayNode && p.relayNode !== 0 ? p.relayName : p.fromName;
          color = getNodeColor(nodeId, nodeName);
        }

        let entry = nodeMap.get(nodeId);
        if (!entry) {
          entry = {
            nodeId,
            nodeName,
            count: 0,
            maxSnr: p.snr,
            latestRssi: p.rssi,
            color
          };
          nodeMap.set(nodeId, entry);
        }

        entry.count++;
        if (p.snr > entry.maxSnr) {
          entry.maxSnr = p.snr;
          entry.latestRssi = p.rssi;
        }

        if (p.snr > cellMaxSnr) {
          cellMaxSnr = p.snr;
        }
        snrSum += p.snr;
      }

      // Sort nodes in cell by maximum SNR descending (signal-strongest transmitting node first)
      const nodesSummary = Array.from(nodeMap.values()).sort((a, b) =>
        b.maxSnr !== a.maxSnr ? b.maxSnr - a.maxSnr : b.count - a.count
      );
      const dominant = nodesSummary[0];
      const avgSnr = Number((snrSum / cell.packets.length).toFixed(1));
      const opacity = snrToOpacity(dominant.maxSnr);

      // The hexagon strictly adopts the color of the signal-strongest transmitting node
      const cellColor = dominant.color;

      result.push({
        id: key,
        zoom,
        q: cell.q,
        r: cell.r,
        center,
        polygon,
        packets: cell.packets,
        packetCount: cell.packets.length,
        dominantNodeId: dominant.nodeId,
        dominantNodeName: dominant.nodeName,
        color: cellColor,
        maxSnr: cellMaxSnr,
        avgSnr,
        opacity,
        isMultiNode: nodesSummary.length > 1,
        nodesSummary
      });
    }

    return result;
  }
}
